import React from "react";
import { Link } from "react-router-dom";

import { percentage } from "lib/common";

import {
  useRecoilValue,
  useSetRecoilState,
  useRecoilValueLoadable,
} from "recoil";
import {
  asyncGetResentJob,
  createSentiment,
  getJob,
} from "lib/store/sentiment";
import { customToastState, errorToastState } from "lib/store/common";
import { currentProject } from "lib/store/project";

import { Button, Checkbox, Radio } from "@atsent/components";

import { Spinner } from "components/atom/Spinner";
import { FileTable } from "./FileTable";
import { Upload } from "./Upload";

import "./Batch.scss";

export type BatchProps = {
  cx?: string;
  pid: any;
  job: any;
  setErrorToast: any;
  setToast: any;
};

export type BatchState = {
  loading: boolean;
  select: number;
  currentFile: string[][] | null;
  progress: number;
  curState: "ready" | "hasFile" | "progress" | "complete";
  curInterval: any;
  file: {
    title: string;
    data: string[][];
    size: number;
    length: number;
  } | null;
  projectId: number;
  batch: { id: number; name: string; size: number } | null;
};

export const Batch = () => {
  const pid = useRecoilValue(currentProject);
  const job = useRecoilValueLoadable(asyncGetResentJob);
  const setErrorToast = useSetRecoilState(errorToastState);
  const setToast = useSetRecoilState(customToastState);

  return (
    <BatchClass
      pid={pid}
      job={job}
      setErrorToast={setErrorToast}
      setToast={setToast}
    />
  );
};

class BatchClass extends React.Component<BatchProps, BatchState> {
  constructor(props: BatchProps) {
    super(props);

    this.state = {
      currentFile: null,
      loading: false,
      select: -1,
      file: null,
      progress: 0,
      curInterval: null,
      projectId: 0,
      batch: null,
      curState: "ready",
    };

    this.setLoading = this.setLoading.bind(this);
    this.setProgress = this.setProgress.bind(this);
    this.setFile = this.setFile.bind(this);
    this.setCurrentFile = this.setCurrentFile.bind(this);
    this.setCurState = this.setCurState.bind(this);
    this.setSelect = this.setSelect.bind(this);

    this.handleRefresh = this.handleRefresh.bind(this);
    this.handleDeleteBatch = this.handleDeleteBatch.bind(this);
    this.handleClickButton = this.handleClickButton.bind(this);
  }

  setLoading = (val: boolean) => this.setState((_) => ({ ..._, loading: val }));
  setProgress = (val: number) =>
    this.setState((_) => ({ ..._, progress: val }));
  setFile = (val: any) => this.setState((_) => ({ ..._, file: val }));
  setCurrentFile = (val: any) =>
    this.setState((_) => ({
      ..._,
      currentFile: val,
    }));
  setSelect = (val: number) => this.setState((_) => ({ ..._, select: val }));
  setCurState = (val: "ready" | "hasFile" | "progress" | "complete") =>
    this.setState((_) => ({
      ..._,
      curState: val,
    }));
  setCurInterval = (val: any) =>
    this.setState((_) => ({ ..._, curInterval: val }));

  handleClickButton = async () => {
    const { setLoading, handleRefresh, handleDeleteBatch } = this;
    const { pid, setErrorToast } = this.props;
    const { file, select } = this.state;
    if (!file) return;
    try {
      await setLoading(true);
      const { id } = await createSentiment(
        pid,
        file.data.map((i) => i[select]).filter((i) => !!i && i.length > 0)
      );
      const batchs = localStorage.getItem("batch") ?? "{}";
      const returnVal = JSON.parse(batchs);
      returnVal[`${pid}`] = { id, name: file.title, size: file.size };
      localStorage.setItem("batch", JSON.stringify(returnVal));
      await handleRefresh(id);
    } catch (e) {
      setErrorToast(e);
      handleDeleteBatch();
      setLoading(false);
    }
  };

  handleDeleteBatch = () => {
    const { pid } = this.props;
    let returnVal: any = {};
    const batchData = localStorage.getItem("batch");
    if (!!batchData) {
      returnVal = JSON.parse(batchData);
    }
    if (Object(returnVal).hasOwnProperty(`${pid}`)) {
      delete returnVal[`${pid}`];
    }
    localStorage.setItem("batch", JSON.stringify(returnVal));
  };

  handleRefresh = async (id: number) => {
    const {
      setLoading,
      setCurState,
      setProgress,
      setCurInterval,
      handleDeleteBatch,
    } = this;
    const { pid, setErrorToast } = this.props;
    const { curInterval } = this.state;
    try {
      const { total, done } = await getJob(pid, id);
      setProgress((done / total) * 100);
      const interval = setInterval(async () => {
        setCurInterval(interval);
        const { status, total, done } = await getJob(pid, id);
        setProgress((done / total) * 100);
        if (status !== "IN_PROGRESS" && status !== "READY") {
          await clearInterval(interval);
          await setLoading(false);
          if (status === "DONE") {
            setCurState("complete");
            handleDeleteBatch();
          }
        }
      }, 2000);
    } catch (e) {
      clearInterval(curInterval);
      handleDeleteBatch();
      setErrorToast(e);
    }
  };

  componentDidMount() {
    const { handleRefresh } = this;
    const { pid } = this.props;
    const batchData = localStorage.getItem("batch") ?? "{}";
    const data = JSON.parse(batchData);
    if (Object(data).hasOwnProperty(`${pid}`)) {
      const { id, name, size } = data[`${pid}`];
      this.setState((_) => ({ ..._, batch: { id, name, size } }));
      handleRefresh(id);
    }
  }

  componentWillUnmount() {
    clearInterval(this.state.curInterval);
  }

  render() {
    const {
      setLoading,
      setFile,
      setCurrentFile,
      setSelect,
      setCurState,
      handleClickButton,
    } = this;
    const { cx, job, pid, setToast } = this.props;
    const { file, loading, progress, currentFile, select, batch, curState } =
      this.state;
    return (
      <section className={`Batch ${cx}`}>
        <span className={"title"}>
          다수의 글로부터 전체적인 감성을 파악해 보세요.
        </span>
        <span className={"number"}>1. CSV 파일을 업로드 해주세요.</span>
        {!file && !batch?.id && !progress && (
          <Upload
            loading={loading}
            setLoading={setLoading}
            setFile={setFile}
            setCurrentFile={setCurrentFile}
          />
        )}
        {!!batch?.id && !file && (
          <>
            <FileTable
              title={batch?.name ?? ""}
              size={batch?.size ?? 0}
              closeClick={() => {
                if (progress > 0 && curState !== "complete") {
                  setToast({
                    type: "error",
                    text: "시작한 작업은 취소할 수 없습니다.",
                  });
                } else {
                  window.location.reload();
                }
              }}
              state={
                curState === "complete"
                  ? "완료"
                  : `분석중 ${
                      !!progress
                        ? progress.toFixed(2)
                        : percentage(job.contents.done / job.contents.total)
                    }%`
              }
            />
            <Button to={`/${pid}`} routerLink={Link} isNext>
              대시보드
            </Button>
          </>
        )}
        {!!file && (
          <>
            <FileTable
              title={file.title}
              size={file.size}
              closeClick={() => {
                if (progress > 0 && curState !== "complete") {
                  setToast({
                    type: "error",
                    text: "시작한 작업은 취소할 수 없습니다.",
                  });
                } else {
                  window.location.reload();
                }
              }}
              state={
                loading
                  ? progress.toFixed(0) === "0"
                    ? "대기중"
                    : `분석중 ${progress.toFixed(2)}%`
                  : curState === "complete"
                  ? "완료"
                  : ""
              }
            />
            {curState !== "progress" && curState !== "complete" && (
              <>
                <div className="number-wrapper">
                  <span className={"number"}>
                    2. 분석할 데이터 컬럼을 선택하세요.
                  </span>

                  <Checkbox
                    label={"첫 라인 미포함"}
                    onChange={(e: any) => {
                      if (e.target.checked && currentFile) {
                        setFile({ ...file, data: currentFile.slice(1) });
                      } else {
                        if (!currentFile) return;
                        setFile({ ...file, data: currentFile });
                      }
                    }}
                  />
                </div>
                <form
                  className="DataColumnWrapper"
                  onChange={(e: any) => setSelect(e.target?.value)}
                >
                  <table
                    className={"DataColumn"}
                    style={{ width: 305 * file.length + "px" }}
                  >
                    <thead>
                      <tr>
                        {Array(file.length)
                          .fill("")
                          .map((i, idx) => (
                            <th key={`${idx}-radio`}>
                              <Radio
                                name={"table"}
                                value={idx}
                                key={`${file.title}-${idx}-radio`}
                                noBorder
                              />
                            </th>
                          ))}
                      </tr>
                    </thead>
                    <tbody>
                      {file.data.map((i, idx) => (
                        <tr key={`tr-${idx}`}>
                          {i.map((j, jdx) => (
                            <td key={`td-${jdx}`}>{j}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div
                    className="DataColumnGlider"
                    style={{ transform: `translateX(${305 * select}px)` }}
                  />
                </form>
                <div className="gradient">
                  <span>데이터는 파일에서 확인 해 주세요.</span>
                </div>
              </>
            )}
            {curState === "complete" ? (
              <Button to={`/${pid}`} routerLink={Link} isNext>
                대시보드
              </Button>
            ) : (
              <>
                {loading ? (
                  <Button
                    cx={"LoadingButton"}
                    to={`/${pid}`}
                    routerLink={Link}
                    isNext
                  >
                    <Spinner bgColor={"black"} />
                    <span> 대시보드</span>
                  </Button>
                ) : (
                  <Button
                    disabled={select < 0}
                    click={async () => {
                      await handleClickButton();
                      await setCurState("progress");
                    }}
                    isNext
                  >
                    배치 처리
                  </Button>
                )}
              </>
            )}
          </>
        )}
      </section>
    );
  }
}
